import React, { useState } from "react";
import styled from "styled-components";

import { Button, Tag } from "../../../../design-system";
import { Community } from "../../../../types/Community";
import {
  useSiteConfig,
  useSitePages,
  setSiteEnabled,
} from "../../../../database/landingPage/sitePagesApi";

type Props = {
  communityId?: string;
  community?: Community;
};

const Panel = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;
const Title = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;
const Intro = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin: 0;
  max-width: 720px;
  line-height: 1.6;
`;
const Table = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  overflow: hidden;
`;
const HeadRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 80px auto;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface-subtle);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;
const BodyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 80px auto;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  align-items: center;
  border-top: 1px solid var(--color-border);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;
const Muted = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
`;
const Empty = styled.div`
  padding: var(--space-5);
  text-align: center;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;
const Warn = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-warning-text, #9a6700);
`;

const WebsitePanel = ({ communityId, community }: Props) => {
  const config = useSiteConfig({ communityId });
  const pages = useSitePages({ communityId });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled = config?.enabled === true;
  const slug = (community?.slug || communityId || "").trim();

  const onToggle = async () => {
    if (!communityId) return;
    if (!enabled && pages.length === 0) {
      setError("Author at least one page (via Claude / MCP: upsert_site_page) before turning the website on.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setSiteEnabled(communityId, !enabled);
    } catch (e: any) {
      setError(e?.message || "Could not update website mode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <Header>
        <Title>Website mode</Title>
        <Tag variant={enabled ? "success" : "neutral"} size="sm" withDot>
          {enabled ? "Website active" : "Landing only"}
        </Tag>
      </Header>
      <Intro>
        Upgrade the single landing page into a full multi-page website (Shop,
        Book, About, Resources, Articles) under a shared nav. Pages are authored
        by Claude via MCP (upsert_site_page). Turn this on once your pages are
        ready.
      </Intro>

      <Table>
        <HeadRow>
          <span>Page</span>
          <span>Nav label</span>
          <span>Status</span>
          <span />
        </HeadRow>
        {pages.length === 0 ? (
          <Empty>No pages yet. Ask Claude to build your Shop / Book / About / Resources pages.</Empty>
        ) : (
          pages.map((p) => (
            <BodyRow key={p.pageKey}>
              <span>{p.title || p.pageKey}</span>
              <span>{p.navLabel || "—"}</span>
              <span>{p.enabled === false ? <Muted>Hidden</Muted> : "Live"}</span>
              <span>
                {slug && (
                  <a href={`https://tribed.io/c/${slug}/${p.pageKey}`} target="_blank" rel="noreferrer" style={{ color: "var(--color-accent)", fontSize: "var(--font-size-xs)" }}>
                    Open
                  </a>
                )}
              </span>
            </BodyRow>
          ))
        )}
      </Table>

      {error && <Warn>{error}</Warn>}
      <div>
        <Button variant={enabled ? "secondary" : "primary"} onClick={onToggle} disabled={busy || !communityId}>
          {busy ? "Updating..." : enabled ? "Turn website off" : "Turn website on"}
        </Button>
      </div>
    </Panel>
  );
};

export default WebsitePanel;
